import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";

/** Every state a round can be filtered by. */
const DRAW_STATUSES = ["open", "locked", "drawn", "settled", "void"] as const;
import { useAuth } from "../context/auth";
import { useToast } from "../context/toast";
import {money, signedMoney} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import { realtime } from "../lib/ws";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  DrawStatusBadge,
  EmptyState,
  ErrorNote,
  Field,
  LoadingRows,
  BackfilledBadge,
  ManualBadge,
  Modal,
  NumberChip,
  Panel,
  RowActions,
  rowActionClass,
  StatTile,
  Table,
  Td,
  Tr,
} from "../components/ui";
import { Checkbox, NumberSlots, TextAreaField } from "../components/ui/form";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { AdminDraw, DrawSummary, Paged, ResultEditResult } from "../types/api";

export function Draws() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();
  const { can } = useAuth();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const filters = useFilters(
    { status: "", drawNo: "", result: "", from: "", to: "" },
    () => setPage(1),
  );
  const sort = useSort("drawNo", "desc", () => setPage(1));

  const draws = useLive<Paged<AdminDraw> & { summary: DrawSummary }>("draws", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        status: filters.applied.status || undefined,
        drawNo: filters.applied.drawNo || undefined,
        result: filters.applied.result || undefined,
        from: filters.applied.from
          ? new Date(filters.applied.from).toISOString()
          : undefined,
        to: filters.applied.to
          ? new Date(`${filters.applied.to}T23:59:59`).toISOString()
          : undefined,
      });
  const [resultTarget, setResultTarget] = useState<AdminDraw | null>(null);
  const [voidTarget, setVoidTarget] = useState<AdminDraw | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminDraw | null>(null);

  // A settled round changes this list, so refresh when one lands.
  useEffect(
    () =>
      realtime.subscribe((event) => {
        if (event === "draw.settled") draws.reload();
      }),
    [draws],
  );

  return (
    <div className="space-y-5">
      <FilterBar
        loading={draws.loading}
        state={filters}
        filters={[
          { key: "drawNo", label: t("common.round"), placeholder: "e.g. 2270" },
          {
            key: "status",
            label: t("common.status"),
            type: "select",
            options: [
              { value: "", label: t("common.anyStatus") },
              ...DRAW_STATUSES.map((v) => ({
                value: v,
                label: t(`drawStatus.${v}` as TranslationKey),
              })),
            ],
          },
          {
            key: "result",
            label: t("draws.filter.result"),
            type: "select",
            options: [
              { value: "", label: t("draws.filter.anyResult") },
              { value: "win", label: t("draws.filter.houseWon") },
              { value: "loss", label: t("draws.filter.houseLost") },
              { value: "even", label: t("draws.filter.breakEven") },
            ],
          },
          { key: "from", label: t("draws.filter.closedFrom"), type: "date" },
          { key: "to", label: t("draws.filter.closedTo"), type: "date" },
        ]}
      />

      <div className="space-y-5">
        {draws.data ? (
          <DrawSummaryStrip
            summary={draws.data.summary}
            total={draws.data.total}
          />
        ) : null}

        <Panel
          eyebrow={t("section.rounds")}
          title={t("draws.title")}
          bodyClassName=""
          actions={
            <Button size="sm" onClick={draws.reload}>
              {t("action.refresh")}
            </Button>
          }
        >
          {draws.error ? (
            <div className="p-5">
              <ErrorNote message={draws.error} onRetry={draws.reload} />
            </div>
          ) : (
            <>
              <Table
                minWidth="76rem"
                head={[
                  <SortHeader key="d" field="drawNo" state={sort}>
                    {t("common.round")}
                  </SortHeader>,
                  <SortHeader key="s" field="status" state={sort}>
                    {t("common.status")}
                  </SortHeader>,
                  t("dashboard.openingNumbers"),
                  <SortHeader
                    key="b"
                    field="betCount"
                    state={sort}
                    align="right"
                  >
                    {t("nav.bets")}
                  </SortHeader>,
                  <SortHeader
                    key="st"
                    field="stakeCents"
                    state={sort}
                    align="right"
                  >
                    {t("col.stake")}
                  </SortHeader>,
                  <SortHeader
                    key="pa"
                    field="payoutCents"
                    state={sort}
                    align="right"
                  >
                    {t("col.payout")}
                  </SortHeader>,
                  <SortHeader
                    key="n"
                    field="netCents"
                    state={sort}
                    align="right"
                  >
                    {t("col.winLoss")}
                  </SortHeader>,
                  <SortHeader key="c" field="closesAt" state={sort}>
                    {t("col.dateTime")}
                  </SortHeader>,
                  <span key="a" className="block text-right">
                    {t("col.actions")}
                  </span>,
                ]}
              >
                {draws.loading ? (
                  <LoadingRows columns={9} />
                ) : draws.data && draws.data.items.length === 0 ? (
                  <Tr>
                    <Td colSpan={9}>
                      <EmptyState
                        title={t("draws.empty")}
                        detail={t("draws.schedulerNote")}
                      />
                    </Td>
                  </Tr>
                ) : (
                  (draws.data?.items ?? []).map((draw) => (
                    <Tr key={draw._id}>
                      <Td className="money accent text-[12px]">
                        #{draw.drawNo}
                      </Td>
                      <Td>
                        <span className="flex items-center gap-1.5">
                          <DrawStatusBadge status={draw.status} />
                          {draw.resultSource === "manual" ? (
                            <ManualBadge />
                          ) : null}
                          {draw.isBackfilled ? <BackfilledBadge /> : null}
                        </span>
                      </Td>
                      <Td>
                        {draw.results.length ? (
                          <span className="flex gap-1.5">
                            {draw.results.map((value, index) => (
                              <NumberChip key={index} value={value} size="sm" />
                            ))}
                          </span>
                        ) : (
                          <span className="text-dim text-[12px]">
                            {t("draws.notDrawn")}
                          </span>
                        )}
                      </Td>
                      <Td className="money text-right">
                        {draw.totals?.betCount ?? 0}
                      </Td>
                      <Td className="money text-dim text-right">
                        {money(draw.totals?.stakeCents ?? 0)}
                      </Td>
                      <Td
                        className="money text-right"
                        style={{ color: "var(--success)" }}
                      >
                        {money(draw.totals?.payoutCents ?? 0)}
                      </Td>
                      <Td className="text-right">
                        <NetAmount cents={draw.totals?.netCents ?? 0} />
                      </Td>
                      <Td>
                        {/* Absolute first — an operator reconciling against another
                            system needs the timestamp, not "3h ago". */}
                        <span className="money block text-[12px]">
                          {dateTime(draw.closesAt)}
                        </span>
                        <span className="text-dim block text-[11px]">
                          {relative(draw.closesAt)}
                        </span>
                      </Td>
                      <Td>
                        <RowActions>
                          {/* A link wearing the chip rather than a button
                              inside one: a `<button>` nested in an `<a>` is
                              invalid markup, and which of the two handles the
                              click is then up to the browser. */}
                          <Link
                            to={`/bets?drawNo=${draw.drawNo}`}
                            className={rowActionClass()}
                          >
                            <span className="row-action-icon" aria-hidden>
                              {ActionIcon.view}
                            </span>
                            {t("nav.bets")}
                          </Link>
                          {can("admin") ? (
                            <>
                              {/* Any round but a voided one can have its numbers
                                  set or corrected — the API re-judges from
                                  scratch. The voided case keeps its chip,
                                  disabled: four rows deep the column would
                                  otherwise change shape row by row. */}
                              <ActionButton
                                icon={ActionIcon.result}
                                tone="primary"
                                disabledWhy={
                                  draw.status === "void"
                                    ? t("draws.cannotSetResult")
                                    : null
                                }
                                onClick={() => setResultTarget(draw)}
                              >
                                {draw.results.length
                                  ? t("audit.draw.result_edit")
                                  : t("draws.setResult")}
                              </ActionButton>
                              <ActionButton
                                icon={ActionIcon.revoke}
                                disabledWhy={
                                  ["open", "locked", "drawn"].includes(draw.status)
                                    ? null
                                    : t("draws.cannotVoid")
                                }
                                onClick={() => setVoidTarget(draw)}
                              >
                                {t("draws.void")}
                              </ActionButton>
                              <ActionButton
                                icon={ActionIcon.delete}
                                tone="danger"
                                onClick={() => setDeleteTarget(draw)}
                              >
                                {t("action.delete")}
                              </ActionButton>
                            </>
                          ) : null}
                        </RowActions>
                      </Td>
                    </Tr>
                  ))
                )}
              </Table>

              {draws.data ? (
                <PageBar
                  page={draws.data.page}
                  pages={draws.data.pages}
                  total={draws.data.total}
                  perPage={perPage}
                  onPage={setPage}
                  onPerPage={(size) => {
                    setPerPage(size);
                    setPage(1);
                  }}
                />
              ) : null}
            </>
          )}
        </Panel>
      </div>

      <VoidDialog
        draw={voidTarget}
        onClose={() => setVoidTarget(null)}
        onDone={(refunded) => {
          toast.success(
            t("toast.roundVoided"),
            `${refunded} stake${refunded === 1 ? "" : "s"} refunded.`,
          );
          draws.reload();
        }}
      />
      <SetResultDialog
        draw={resultTarget}
        onClose={() => setResultTarget(null)}
        onDone={(result) => {
          const bets = result.betsRejudged;
          toast.success(
            result.wasSettled ? t("toast.resultChanged") : t("toast.resultSet"),
            bets === 0
              ? t("toast.noBets")
              : `${bets} bet${bets === 1 ? "" : "s"} re-judged, ${result.betsChanged} changed outcome.`,
          );
          draws.reload();
        }}
      />
      <DeleteDrawDialog
        draw={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDone={(removed) => {
          toast.success(
            t("toast.roundDeleted"),
            `${removed} bets removed and reversed.`,
          );
          draws.reload();
        }}
      />
    </div>
  );
}

/**
 * A round's net result, from the house's side.
 *
 * Positive means the round took more than it paid out; negative means players
 * came out ahead. Same sign convention as the Reports page, so the two screens
 * never disagree about which way is up.
 */
function NetAmount({ cents }: { cents: number }) {
  if (cents === 0) return <span className="money text-dim">—</span>;
  return (
    <span
      className="money"
      style={{ color: cents > 0 ? "var(--success)" : "var(--danger)" }}
    >
      {signedMoney(cents)}
    </span>
  );
}

/** Totals across everything the filter matched, not just the page on screen. */
function DrawSummaryStrip({
  summary,
  total,
}: {
  summary: DrawSummary;
  total: number;
}) {
  const t = useT();
  const won = summary.netCents > 0;
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatTile
        label={t("draws.roundsMatched")}
        value={total.toLocaleString()}
        hint={`${summary.betCount.toLocaleString()} bets`}
        tone="plain"
      />
      <StatTile
        label={t("col.staked")}
        value={money(summary.stakeCents)}
        hint={t("draws.takenFromPlayers")}
        tone="gold"
      />
      <StatTile
        label={t("dashboard.paidOut")}
        value={money(summary.payoutCents)}
        hint={t("draws.returnedToWinners")}
        tone="info"
      />
      <StatTile
        label={won ? t("draws.filter.houseWon") : t("draws.filter.houseLost")}
        value={signedMoney(summary.netCents)}
        hint={t("draws.stakedMinusPaid")}
        tone={won ? "success" : "danger"}
        dot
      />
    </div>
  );
}

/**
 * Sets a round's winning numbers — first time or a correction.
 *
 * One dialog covers both because the API does: it re-judges every bet against
 * the numbers you give it and moves the difference through the ledger. What
 * changes between the two cases is the warning, and a round that has already
 * paid out earns a much louder one.
 */
function SetResultDialog({
  draw,
  onClose,
  onDone,
}: {
  draw: AdminDraw | null;
  onClose: () => void;
  onDone: (result: ResultEditResult) => void;
}) {
  const t = useT();
  const [numbers, setNumbers] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** A round that has paid out is the dangerous case; a fresh one is routine. */
  const alreadyPaid = Boolean(draw && draw.results.length > 0);

  useEffect(() => {
    if (!draw) return;
    // A never-drawn round starts with one empty box per position.
    setNumbers(
      draw.results.length
        ? draw.results.map(String)
        : Array(draw.positionCount).fill(""),
    );
    setReason("");
    setConfirmed(false);
    setError(null);
  }, [draw]);

  const min = draw?.numberMin ?? 1;
  const max = draw?.numberMax ?? 10;
  const parsed = numbers.map((value) => Number.parseInt(value, 10));
  const complete =
    numbers.length > 0 &&
    parsed.every(
      (value) => Number.isInteger(value) && value >= min && value <= max,
    );
  const unchanged =
    alreadyPaid && draw ? parsed.join(",") === draw.results.join(",") : false;
  // Only a round that has paid out needs the extra acknowledgement.
  const acknowledged = confirmed || !alreadyPaid;

  async function submit() {
    if (!draw || !complete || !acknowledged || !reason.trim() || unchanged)
      return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.setResult(draw._id, parsed, reason.trim());
      onDone(result);
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.setResult"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(draw)}
      onClose={onClose}
      eyebrow={
        alreadyPaid ? t("draws.correctionEyebrow") : t("section.rounds")
      }
      title={
        alreadyPaid
          ? t("draws.changeTitle", { drawNo: draw?.drawNo ?? "" })
          : t("draws.setTitle", { drawNo: draw?.drawNo ?? "" })
      }
      width="34rem"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant={alreadyPaid ? "danger" : "gold"}
            onClick={submit}
            loading={busy}
            disabled={!complete || !acknowledged || !reason.trim() || unchanged}
          >
            {alreadyPaid
              ? t("draws.changeSubmit")
              : t("draws.setSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {alreadyPaid ? (
          <div
            className="hazard rounded-lg border p-3"
            style={{ borderColor: "var(--warning)" }}
          >
            <p
              className="text-[12px] font-semibold"
              style={{ color: "var(--warning)" }}
            >
{t("draws.paidWarn")}
            </p>
            <p className="text-muted mt-1.5 text-[12px]">
{t("draws.paidExplain")}
            </p>
          </div>
        ) : (
          <p className="text-muted text-[12px]">
{t("draws.freshExplain")}
          </p>
        )}

        {alreadyPaid ? (
          <div>
            <p className="eyebrow mb-1.5">{t("draws.currentResult")}</p>
            <div className="flex gap-1.5">
              {(draw?.results ?? []).map((value, index) => (
                <NumberChip key={index} value={value} size="sm" />
              ))}
            </div>
          </div>
        ) : null}

        <Field
          label={`${alreadyPaid ? t("draws.newNumbers") : t("draws.winningNumbers")} — ${draw?.positionCount ?? 0} positions, ${min} to ${max}`}
          required
        >
          <NumberSlots
            values={numbers}
            onChange={setNumbers}
            min={min}
            max={max}
            ariaLabel={t("bets.edit.position")}
          />
        </Field>

        {unchanged && complete ? (
          <p className="text-dim text-[12px]">
            {t("draws.unchanged")}
          </p>
        ) : null}

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={
            alreadyPaid
              ? t("draws.reasonCorrect")
              : t("draws.reasonSheet")
          }
          required
        />

        {alreadyPaid ? (
          <Checkbox
            checked={confirmed}
            onChange={setConfirmed}
            label={t("draws.acknowledge")}
          />
        ) : null}

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function DeleteDrawDialog({
  draw,
  onClose,
  onDone,
}: {
  draw: AdminDraw | null;
  onClose: () => void;
  onDone: (removed: number) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!draw || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteDraw(draw._id, reason.trim());
      onDone(result.betsRemoved);
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.deleteRound"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(draw)}
      onClose={close}
      eyebrow={t("section.rounds")}
      title={t("draws.deleteRoundTitle", { drawNo: draw?.drawNo ?? "" })}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={!reason.trim()}
          >
            {t("draws.deleteSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
          Removes the round and its {draw?.totals?.betCount ?? 0} bets. Every
          stake is returned and every payout taken back first, so players end
          where they began and the ledger stays consistent.
        </p>
        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("draws.deleteReason")}
          required
        />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function VoidDialog({
  draw,
  onClose,
  onDone,
}: {
  draw: AdminDraw | null;
  onClose: () => void;
  onDone: (refunded: number) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!draw || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.voidDraw(draw._id, reason.trim());
      onDone(result.refunded);
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.voidRound"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(draw)}
      onClose={close}
      eyebrow={t("section.rounds")}
      title={t("draws.voidRoundTitle", { drawNo: draw?.drawNo ?? "" })}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={!reason.trim()}
          >
            {t("draws.voidSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
{t("draws.voidExplain")}
        </p>
        <p className="text-dim text-[12px]">
          <Badge tone="neutral">
            {t("draws.betsOnRound", { count: draw?.totals?.betCount ?? 0 })}
          </Badge>{" "}
          {t("draws.currentlyOnRound")}
        </p>
        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("draws.voidReason")}
          required
        />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
